import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ASSISTED_GOAL_COLOR,
  FIELD_GEOMETRY,
  FIELD_YARDS,
  OUTCOME_STYLE,
  ShotOutcome,
  ShotZone,
  ZONE_LABELS,
  clampShotPoint,
  shotDistanceYards,
} from '../../utils/shotLocation';

// Drawn to real NCAA proportions: from just behind the goal line (top) to a
// few yards past the restraining line (bottom), cropped to the 40yd attack
// area (+2yd margin). 10 SVG units per yard on both axes keeps the crease a
// true circle.
const YARD = 10;
const VIEW_W = FIELD_GEOMETRY.widthYards * YARD;
const VIEW_H = FIELD_GEOMETRY.depthYards * YARD;

export interface FieldMarker {
  key: string;
  x: number;
  y: number;
  outcome: ShotOutcome;
  /** Goals only — draws the gold "assisted" goal instead of the green unassisted one. */
  assisted?: boolean;
  /** Draws a ring so the tapped shot reads as selected. */
  selected?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
  /** Shown beside the dot when this marker is selected. */
  detail?: { heading: string; lines: string[]; color: string };
}

export interface FieldZoneFill {
  color: string;
  opacity: number;
  /** Optional stat caption drawn in the middle of the zone, e.g. "4/9 · 44%". */
  label?: string;
}

interface LacrosseAttackFieldProps {
  /** Show dashed zone grid + zone names — on for the tap picker. */
  showZoneGuides?: boolean;
  /** Show the dashed zone grid without names — used by the shot chart views. */
  showZoneGrid?: boolean;
  /** Enable tap/drag-to-pin; calls onTap with normalized (x, y) in [0,1]. */
  interactive?: boolean;
  /** Fires continuously while pressing/dragging — use it to preview the pin. */
  onTap?: (x: number, y: number) => void;
  /** Fires once on release, after a press that can still be dragged. */
  onCommit?: (x: number, y: number) => void;
  /** Single crosshair pin, e.g. while placing a shot. */
  pin?: { x: number; y: number; outcome?: ShotOutcome | null } | null;
  /** Dots rendered on the field, e.g. a game's located shots. */
  markers?: FieldMarker[];
  /** Zone tile heat fill (volume/efficiency), e.g. a season view. */
  zoneFill?: Partial<Record<ShotZone, FieldZoneFill>>;
  /** Small caption drawn above the field, e.g. "US ATTACK". */
  label?: string;
  className?: string;
}

const g = FIELD_GEOMETRY;

const px = (x: number) => x * VIEW_W;
const py = (y: number) => y * VIEW_H;
const yd = (yards: number) => yards * YARD;

const goalLinePy = py(g.goalLineY);
const goalCenterPx = px(g.goalCenterX);
const creaseR = yd(FIELD_YARDS.creaseRadius);

const rectPath = (x0: number, y0: number, x1: number, y1: number) =>
  `M${px(x0)},${py(y0)} H${px(x1)} V${py(y1)} H${px(x0)} Z`;

// The half of the crease in front of the goal line — the only part a shot can come from.
const CREASE_FRONT_PATH = `M${goalCenterPx - creaseR},${goalLinePy} A${creaseR},${creaseR} 0 0 0 ${
  goalCenterPx + creaseR
},${goalLinePy} Z`;

// Mirrors classifyShotLocation's bands — used for heat tiles so the picture
// always matches how a tap is classified. The slot tile has the crease cut out
// (even-odd) so the two never overlap and blend.
const ZONE_PATHS: Record<ShotZone, { d: string; evenOdd?: boolean }> = {
  crease: { d: CREASE_FRONT_PATH },
  slot: { d: `${rectPath(g.hashLeftX, g.goalLineY, g.hashRightX, g.slotEndY)} ${CREASE_FRONT_PATH}`, evenOdd: true },
  left_alley: { d: rectPath(0, g.goalLineY, g.hashLeftX, g.slotEndY) },
  right_alley: { d: rectPath(g.hashRightX, g.goalLineY, 1, g.slotEndY) },
  left_wing: { d: rectPath(0, g.slotEndY, g.hashLeftX, g.wingEndY) },
  high_slot: { d: rectPath(g.hashLeftX, g.slotEndY, g.hashRightX, g.wingEndY) },
  right_wing: { d: rectPath(g.hashRightX, g.slotEndY, 1, g.wingEndY) },
  left_high: { d: rectPath(0, g.wingEndY, g.hashLeftX, 1) },
  high: { d: rectPath(g.hashLeftX, g.wingEndY, g.hashRightX, 1) },
  right_high: { d: rectPath(g.hashRightX, g.wingEndY, 1, 1) },
};

const yAtYards = (yardsPastGoalLine: number) => g.goalLineY + yardsPastGoalLine / g.depthYards;
const LEFT_BAND_X = g.hashLeftX / 2;
const RIGHT_BAND_X = (g.hashRightX + 1) / 2;
const MID_ROW_Y = (g.slotEndY + g.wingEndY) / 2;
const TOP_ROW_Y = (g.wingEndY + 1) / 2;

const ZONE_LABEL_POINT: Record<ShotZone, { x: number; y: number }> = {
  crease: { x: g.goalCenterX, y: yAtYards(1.6) },
  slot: { x: g.goalCenterX, y: yAtYards(5.6) },
  left_alley: { x: LEFT_BAND_X, y: yAtYards(4) },
  right_alley: { x: RIGHT_BAND_X, y: yAtYards(4) },
  left_wing: { x: LEFT_BAND_X, y: MID_ROW_Y },
  high_slot: { x: g.goalCenterX, y: MID_ROW_Y },
  right_wing: { x: RIGHT_BAND_X, y: MID_ROW_Y },
  left_high: { x: LEFT_BAND_X, y: TOP_ROW_Y },
  high: { x: g.goalCenterX, y: TOP_ROW_Y },
  right_high: { x: RIGHT_BAND_X, y: TOP_ROW_Y },
};

const TURF = '#5fb46f';
const LINE = '#f4fbf5';

const OutcomeGlyph: React.FC<{ outcome: ShotOutcome; assisted?: boolean; size?: number; opacity?: number }> = ({
  outcome,
  assisted = false,
  size = 9.5,
  opacity = 1,
}) => {
  const style = OUTCOME_STYLE[outcome];
  if (style.shape === 'circle') {
    const fill = outcome === 'goal' && assisted ? ASSISTED_GOAL_COLOR : style.color;
    return <circle r={size} fill={fill} opacity={opacity} stroke="#12331c" strokeWidth={1.5} />;
  }
  if (style.shape === 'diamond') {
    const d = size * 1.2;
    return (
      <polygon
        points={`0,${-d} ${d},0 0,${d} ${-d},0`}
        fill={style.color}
        opacity={opacity}
        stroke="white"
        strokeWidth={2}
      />
    );
  }
  if (style.shape === 'square') {
    return (
      <rect
        x={-size * 0.85}
        y={-size * 0.85}
        width={size * 1.7}
        height={size * 1.7}
        fill={style.color}
        opacity={opacity}
        stroke="white"
        strokeWidth={2}
      />
    );
  }
  // x — white halo strokes underneath keep amber legible on turf
  const d = size * 0.85;
  return (
    <g opacity={opacity} strokeLinecap="round">
      <g stroke="white" strokeWidth={7}>
        <line x1={-d} y1={-d} x2={d} y2={d} />
        <line x1={-d} y1={d} x2={d} y2={-d} />
      </g>
      <g stroke={style.color} strokeWidth={4}>
        <line x1={-d} y1={-d} x2={d} y2={d} />
        <line x1={-d} y1={d} x2={d} y2={-d} />
      </g>
    </g>
  );
};

const LacrosseAttackField: React.FC<LacrosseAttackFieldProps> = ({
  showZoneGuides = false,
  showZoneGrid = false,
  interactive = false,
  onTap,
  onCommit,
  pin,
  markers = [],
  zoneFill,
  label,
  className = '',
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const dismissCardRef = useRef<(() => void) | null>(null);
  const uid = useRef(`fld-${Math.random().toString(36).slice(2)}`).current;
  const openMarker = markers.find((m) => m.selected && m.detail);
  dismissCardRef.current = openMarker?.onClick ?? null;

  useEffect(() => {
    if (!openMarker) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && cardRef.current?.contains(target)) return;
      dismissCardRef.current?.();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openMarker]);
  const [dragging, setDragging] = useState(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  const goalMouthHalf = yd(FIELD_YARDS.goalMouthWidth / 2);
  const netApexY = goalLinePy - yd(FIELD_YARDS.goalNetDepth);
  const restrainingPy = py(g.restrainingY);

  const pointToField = useCallback((clientX: number, clientY: number, el: SVGSVGElement) => {
    const rect = el.getBoundingClientRect();
    return clampShotPoint((clientX - rect.left) / rect.width, (clientY - rect.top) / rect.height);
  }, []);

  // Press to place, drag to fine-tune, release to commit — much easier than
  // trying to hit an exact spot on a phone with a single blind tap.
  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;
    // Swallow the gesture so the tap cannot click a control under the field
    // once a sheet closes.
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    const p = pointToField(e.clientX, e.clientY, e.currentTarget);
    lastPoint.current = p;
    onTap?.(p.x, p.y);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive || !dragging) return;
    const p = pointToField(e.clientX, e.clientY, e.currentTarget);
    lastPoint.current = p;
    onTap?.(p.x, p.y);
  };

  const endDrag = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragging) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    e.preventDefault();
    e.stopPropagation();
    const p = lastPoint.current;
    if (p && e.type === 'pointerup') onCommit?.(p.x, p.y);
    lastPoint.current = null;
  };

  // Half-yard nudges for precise placement / accessibility.
  const handleKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
    if (!interactive) return;
    const stepX = 0.5 / g.widthYards;
    const stepY = 0.5 / g.depthYards;
    const current = pin ?? { x: g.goalCenterX, y: g.slotEndY };
    let next: { x: number; y: number } | null = null;

    if (e.key === 'ArrowLeft') next = { x: current.x - stepX, y: current.y };
    else if (e.key === 'ArrowRight') next = { x: current.x + stepX, y: current.y };
    else if (e.key === 'ArrowUp') next = { x: current.x, y: current.y - stepY };
    else if (e.key === 'ArrowDown') next = { x: current.x, y: current.y + stepY };
    else if (e.key === 'Enter' || e.key === ' ') next = current;

    if (!next) return;
    e.preventDefault();
    const { x, y } = clampShotPoint(next.x, next.y);
    onTap?.(x, y);
    if (e.key === 'Enter' || e.key === ' ') onCommit?.(x, y);
  };

  const zoneTileNodes = useMemo(() => {
    if (!zoneFill) return null;
    return (Object.keys(ZONE_PATHS) as ShotZone[]).flatMap((zone) => {
      const fill = zoneFill[zone];
      if (!fill) return [];
      const shape = ZONE_PATHS[zone];
      return [
        <path
          key={zone}
          d={shape.d}
          fillRule={shape.evenOdd ? 'evenodd' : undefined}
          fill={fill.color}
          opacity={fill.opacity}
        />,
      ];
    });
  }, [zoneFill]);

  const zoneStatLabels = useMemo(() => {
    if (!zoneFill) return null;
    return (Object.keys(ZONE_PATHS) as ShotZone[]).flatMap((zone) => {
      const fill = zoneFill[zone];
      if (!fill?.label) return [];
      const point = ZONE_LABEL_POINT[zone];
      return [
        <text
          key={`stat-${zone}`}
          x={px(point.x)}
          y={py(point.y)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={zone === 'crease' ? 11 : 13}
          fontWeight={800}
          fill="#0d2f18"
          stroke="white"
          strokeWidth={3}
          paintOrder="stroke"
          pointerEvents="none"
        >
          {fill.label}
        </text>,
      ];
    });
  }, [zoneFill]);

  const pinDistance = pin ? shotDistanceYards(pin.x, pin.y) : null;
  const showGrid = showZoneGuides || showZoneGrid;

  return (
    <div className={`w-full select-none ${className}`}>
      {label && <div className="text-center text-xs font-bold text-[#19133c] tracking-wide mb-1">{label}</div>}
      <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className={`w-full h-auto rounded-xl ${
          interactive
            ? 'border-2 border-[#1f5c33] shadow-sm cursor-crosshair touch-none focus:outline-none focus:ring-2 focus:ring-[#19133c]/20'
            : 'border border-[#d8dee8]'
        }`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        tabIndex={interactive ? 0 : undefined}
        role={interactive ? 'application' : 'img'}
        aria-label={
          interactive
            ? 'Attacking end diagram. Tap or drag to place the shot; arrow keys nudge half a yard.'
            : 'Shot location field diagram'
        }
      >
        <defs>
          <pattern id={`${uid}-net`} width={6} height={6} patternUnits="userSpaceOnUse">
            <rect width={6} height={6} fill="#17331d" opacity={0.4} />
            <line x1={0} y1={0} x2={6} y2={6} stroke="white" strokeWidth={0.9} opacity={0.75} />
            <line x1={6} y1={0} x2={0} y2={6} stroke="white" strokeWidth={0.9} opacity={0.75} />
          </pattern>
          <clipPath id={`${uid}-clip`}>
            <rect x={0} y={0} width={VIEW_W} height={VIEW_H} rx={8} />
          </clipPath>
          <filter id={`${uid}-shadow`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx={0} dy={1.5} stdDeviation={2} floodColor="#0b2412" floodOpacity={0.45} />
          </filter>
        </defs>

        <g clipPath={`url(#${uid}-clip)`}>
          <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill={TURF} />

          {/* Zone heat tiles (season/compare views) */}
          {zoneTileNodes}

          {/* Attack area box: sidelines of the box down to the restraining line */}
          <g stroke={LINE} strokeWidth={3} opacity={0.9} fill="none">
            <line x1={px(g.attackAreaLeftX)} y1={0} x2={px(g.attackAreaLeftX)} y2={restrainingPy} />
            <line x1={px(g.attackAreaRightX)} y1={0} x2={px(g.attackAreaRightX)} y2={restrainingPy} />
          </g>

          {/* Restraining line — spans the full field width */}
          <line x1={0} y1={restrainingPy} x2={VIEW_W} y2={restrainingPy} stroke={LINE} strokeWidth={4} opacity={0.95} />

          {/* GLE (goal line extended) — reference marker; every shot comes from in front of it */}
          <g stroke={LINE} strokeWidth={2.5} strokeDasharray="2 7" strokeLinecap="round" opacity={0.95}>
            <line x1={px(g.attackAreaLeftX)} y1={goalLinePy} x2={goalCenterPx - creaseR} y2={goalLinePy} />
            <line x1={goalCenterPx + creaseR} y1={goalLinePy} x2={px(g.attackAreaRightX)} y2={goalLinePy} />
          </g>
          <text
            x={px(g.attackAreaLeftX) + 6}
            y={goalLinePy - 7}
            fontSize={11}
            fontWeight={800}
            fill={LINE}
            opacity={0.9}
            pointerEvents="none"
          >
            GLE
          </text>

          {/* Coaching hash lane — the 12yd "inside the hashes" shooting lane */}
          <g stroke={LINE} strokeWidth={2.5} opacity={0.75}>
            {[g.hashLeftX, g.hashRightX].map((hx) =>
              [g.slotEndY, g.wingEndY].map((hy) => (
                <line
                  key={`${hx}-${hy}`}
                  x1={px(hx)}
                  y1={py(hy) - yd(0.6)}
                  x2={px(hx)}
                  y2={py(hy) + yd(0.6)}
                />
              )),
            )}
          </g>

          {/* Zone grid — light gray so it stays behind the shot markers */}
          {showGrid && (
            <g stroke="#e4e7ec" strokeDasharray="6 6" strokeWidth={1.5} opacity={0.7}>
              <line x1={px(g.hashLeftX)} y1={goalLinePy} x2={px(g.hashLeftX)} y2={VIEW_H} />
              <line x1={px(g.hashRightX)} y1={goalLinePy} x2={px(g.hashRightX)} y2={VIEW_H} />
              <line x1={0} y1={py(g.slotEndY)} x2={VIEW_W} y2={py(g.slotEndY)} />
              <line x1={0} y1={py(g.wingEndY)} x2={VIEW_W} y2={py(g.wingEndY)} />
            </g>
          )}

          {/* Crease — 9ft radius, never painted (NCAA), so outline only */}
          <circle cx={goalCenterPx} cy={goalLinePy} r={creaseR} fill="none" stroke={LINE} strokeWidth={3} opacity={0.95} />

          {/* The cage, top-down: 6ft mouth tapering to the net point 7ft back.
              The bold mouth bar is what makes it read as a goal at this scale. */}
          <g filter={`url(#${uid}-shadow)`}>
            <polygon
              points={`${goalCenterPx - goalMouthHalf},${goalLinePy} ${goalCenterPx + goalMouthHalf},${goalLinePy} ${goalCenterPx},${netApexY}`}
              fill={`url(#${uid}-net)`}
              stroke="#dfe8e2"
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
            <line
              x1={goalCenterPx - goalMouthHalf}
              y1={goalLinePy}
              x2={goalCenterPx + goalMouthHalf}
              y2={goalLinePy}
              stroke="#f7f7f2"
              strokeWidth={6}
              strokeLinecap="round"
            />
          </g>

          {/* Zone labels — only while placing a shot */}
          {showZoneGuides &&
            (Object.keys(ZONE_LABEL_POINT) as ShotZone[]).map((zone) => {
              const point = ZONE_LABEL_POINT[zone];
              return (
                <text
                  key={zone}
                  x={px(point.x)}
                  y={py(point.y)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={zone === 'crease' ? 10 : 12}
                  fontWeight={700}
                  fill="#0d2f18"
                  opacity={0.72}
                  pointerEvents="none"
                >
                  {ZONE_LABELS[zone]}
                </text>
              );
            })}

          {/* Zone stat captions (charts) */}
          {zoneStatLabels}

          {/* Located shot markers */}
          <g filter={`url(#${uid}-shadow)`}>
            {markers.map((m) => (
              <g
                key={m.key}
                transform={`translate(${px(m.x)}, ${py(m.y)})`}
                onPointerDown={(e) => {
                  if (m.onClick) e.stopPropagation();
                }}
                onClick={(e) => {
                  if (m.onClick) {
                    e.stopPropagation();
                    m.onClick();
                  }
                }}
                style={{ cursor: m.onClick ? 'pointer' : undefined }}
              >
                <circle r={16} fill="transparent" />
                {m.selected && <circle r={15} fill="none" stroke="white" strokeWidth={2.5} />}
                <OutcomeGlyph outcome={m.outcome} assisted={m.assisted} opacity={m.dimmed ? 0.28 : 0.98} />
              </g>
            ))}
          </g>

          {/* Active pin — crosshair, distance badge, and a shot line to the cage */}
          {pin && (
            <g pointerEvents="none">
              <g stroke="white" strokeWidth={1.5} strokeDasharray="7 6" opacity={0.75}>
                <line x1={px(pin.x)} y1={0} x2={px(pin.x)} y2={VIEW_H} />
                <line x1={0} y1={py(pin.y)} x2={VIEW_W} y2={py(pin.y)} />
              </g>
              <line
                x1={px(pin.x)}
                y1={py(pin.y)}
                x2={goalCenterPx}
                y2={goalLinePy}
                stroke="#fff59d"
                strokeWidth={2}
                strokeDasharray="4 4"
                opacity={0.9}
              />
              <g transform={`translate(${px(pin.x)}, ${py(pin.y)})`} filter={`url(#${uid}-shadow)`}>
                <circle
                  r={dragging ? 26 : 21}
                  fill="none"
                  stroke="white"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  opacity={0.85}
                />
                {pin.outcome ? (
                  <OutcomeGlyph outcome={pin.outcome} size={dragging ? 14 : 12} />
                ) : (
                  <circle r={dragging ? 9 : 7} fill="#19133c" stroke="white" strokeWidth={2} />
                )}
              </g>
              {pinDistance !== null && (
                <g
                  transform={`translate(${px(pin.x)}, ${
                    pin.y > 0.85 ? py(pin.y) - yd(dragging ? 4.4 : 3.8) : py(pin.y) + yd(dragging ? 4.4 : 3.8)
                  })`}
                >
                  <rect x={-26} y={-11} width={52} height={22} rx={11} fill="#19133c" opacity={0.9} />
                  <text
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={13}
                    fontWeight={800}
                    fill="white"
                    y={1}
                  >
                    {pinDistance.toFixed(1)} yd
                  </text>
                </g>
              )}
            </g>
          )}
        </g>
      </svg>
      {markers
        .filter((m) => m.selected && m.detail)
        .map((m) => (
          <div
            key={`detail-${m.key}`}
            ref={cardRef}
            className="absolute z-10 w-40 rounded-lg bg-white/95 px-2.5 py-1.5 shadow-md ring-1 ring-[#d8dee8] pointer-events-auto"
            style={{
              left: `${m.x * 100}%`,
              top: `${m.y * 100}%`,
              transform: `translate(${m.x > 0.55 ? 'calc(-100% - 12px)' : '12px'}, ${
                m.y > 0.72 ? 'calc(-100% - 6px)' : '8px'
              })`,
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-1.5">
              <span
                className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: m.detail!.color }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-1">
                  <p className="truncate text-xs font-semibold text-[#19133c]">{m.detail!.heading}</p>
                  <button
                    type="button"
                    aria-label="Close shot details"
                    onClick={m.onClick}
                    className="shrink-0 text-sm leading-none text-[#8a93a6] hover:text-[#19133c]"
                  >
                    ×
                  </button>
                </div>
                {m.detail!.lines.map((line) => (
                  <p key={line} className="text-[11px] leading-snug text-[#6b7288]">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LacrosseAttackField;
export { VIEW_W, VIEW_H };
